/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Zalify has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function initPaypalCheckoutBtn({ btnClass, totalPrice }) {
  const pageType = window.themeVariables.settings.pageType
  if (pageType !== 'cart' && pageType !== 'product') return
  if (!paypal || !btnClass) return
  if (!totalPrice) return

  paypal
    .Buttons({
      style: {
        height: 52,
      },

      createOrder(data, actions) {
        return actions.order.create({
          purchase_units: [
            {
              amount: {
                value: totalPrice,
              },
            },
          ],
        })
      },
    })
    .render(btnClass)
}

const ProductBundles = class extends HTMLElement {
  constructor() {
    super()

    this.formId = this.getAttribute('form-id')
  }

  connectedCallback() {
    const bundleBtns = this.querySelectorAll('.product-form__bundle--item')
    if (bundleBtns && bundleBtns.length) {
      bundleBtns.forEach(btn => {
        btn.addEventListener('click', event => {
          event.stopPropagation()
          bundleBtns.forEach(btn => btn.removeAttribute('active'))
          this._handleAddToCart(event)
        })
      })
    }
  }

  async _handleAddToCart(event) {
    event.target.setAttribute('active', '')

    const quantity = event.target.dataset.quantity
    const inputElement = document.querySelector(
      `.product-form .quantity-selector__input[form="${this.formId}"]`
    )
    if (quantity && inputElement) {
      inputElement.quantity = quantity
    }
  }
}
window.customElements.define('product-bundles', ProductBundles)

document.addEventListener('variant:changed', async function (event) {
  const variant = event.detail.variant
  const formId = event.target.getAttribute('id')
  const bundleElement = formId
    ? document.querySelector(`.product-form__bundle[form-id="${formId}"]`)
    : null

  if (!variant || !bundleElement) return

  if (!variant.available) {
    bundleElement.setAttribute('hidden', '')
    return
  }

  bundleElement.removeAttribute('hidden')

  const url = window.location.href
  const searchParams = new URLSearchParams(url)
  searchParams.set('variant', variant.id)
  url.search = searchParams.toString()

  const response = await fetch(url.toString())
  const fakeDiv = document.createElement('div')
  fakeDiv.innerHTML = await response.text()

  bundleElement.outerHTML = fakeDiv.querySelector(
    `.product-form__bundle[form-id="${formId}"]`
  ).outerHTML
})

document.addEventListener('variant:changed', async function (event) {
  const variant = event.detail.variant
  const formId = event.target.getAttribute('id')
  const paypalCheckoutContainer = formId
    ? document.querySelector(`.paypal-button-container[form-id="${formId}"]`)
    : null

  if (!variant || !paypalCheckoutContainer) return

  if (!variant.available) {
    paypalCheckoutContainer.setAttribute('hidden', '')
    return
  }

  paypalCheckoutContainer.removeAttribute('hidden')
  paypalCheckoutContainer.innerHTML = ''

  initPaypalCheckoutBtn({
    btnClass: `.paypal-button-container[form-id="${formId}"]`,
    totalPrice: variant.price / 100,
  })
})

document.addEventListener('cart:show-and-fetch', async function (event) {
  const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`)
  const cartContent = await response.json()
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:updated', {
      bubbles: true,
      detail: {
        cart: cartContent,
      },
    })
  )
  cartContent['sections'] = event.detail['sections']
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {
        cart: cartContent,
        openMiniCart:
          window.themeVariables.settings.cartType === 'drawer' &&
          event.detail.drawerEl === null,
      },
    })
  )
})
;(function () {
  const collectionHandle = 'hot-product-series'
  const prioritySelector = '[data-chicchok-hot-priority]'
  let hydrationPromise = null
  let hydrationAttempted = false

  function isHotCollectionPage() {
    const path = window.location.pathname.replace(/\/+$/, '')
    return path.includes('/collections/') && path.split('/').pop() === collectionHandle
  }

  function hasPriorityQuery() {
    const firstSegment = window.location.search.replace(/^\?/, '').split('&')[0]
    return Boolean(firstSegment && !firstSegment.includes('='))
  }

  function parsePriorityHandles() {
    const firstSegment = window.location.search.replace(/^\?/, '').split('&')[0]

    if (!firstSegment || firstSegment.includes('=')) return []

    const seen = new Set()

    return firstSegment
      .replace(/%2C/gi, ',')
      .split(',')
      .map(part => {
        try {
          return decodeURIComponent(part)
        } catch (error) {
          return part
        }
      })
      .map(handle => String(handle || '').trim().toLowerCase())
      .filter(handle => {
        if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(handle)) return false
        if (seen.has(handle)) return false
        seen.add(handle)
        return true
      })
      .slice(0, 20)
  }

  function findProductGrid(root = document) {
    return root.querySelector('#facet-main .product-list__inner, .product-list__inner')
  }

  function markPriorityGridReady() {
    document.documentElement.classList.remove('chicchok-hot-priority-pending')
    document.documentElement.classList.add('chicchok-hot-priority-ready')
  }

  async function hydratePriorityGrid() {
    const grid = findProductGrid()

    if (!grid || grid.querySelector(prioritySelector)) return

    const handles = parsePriorityHandles()

    if (!handles.length) return

    const renderUrl = new URL(window.location.href)
    renderUrl.search = `?sort_by=${encodeURIComponent(handles.join(','))}`

    const response = await fetch(renderUrl.href, {
      credentials: 'same-origin',
    })

    if (!response.ok) return

    const html = await response.text()
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const renderedGrid = findProductGrid(doc)

    if (!renderedGrid || !renderedGrid.querySelector(prioritySelector)) return

    grid.innerHTML = renderedGrid.innerHTML
    grid.querySelectorAll('product-item[reveal], .product-item[reveal]').forEach(card => {
      if (!card.style.opacity) card.style.opacity = '1'
    })
  }

  function schedulePriorityHydration() {
    if (hydrationAttempted) return hydrationPromise
    if (hydrationPromise) return hydrationPromise

    hydrationAttempted = true
    hydrationPromise = hydratePriorityGrid()
      .catch(() => {})
      .finally(() => {
        hydrationPromise = null
        markPriorityGridReady()
        repairPriorityInteractions()
      })

    return hydrationPromise
  }

  function clearInlineImageState(wrapper) {
    wrapper.querySelectorAll('[data-chicchok-hot-priority-inline-image]').forEach(image => {
      image.style.opacity = ''
      image.style.visibility = ''
      image.removeAttribute('data-chicchok-hot-priority-inline-image')
    })
  }

  function repairHoverImages(wrapper) {
    const primary =
      wrapper.querySelector('.product-item__primary-image:not([hidden])') ||
      wrapper.querySelector('.product-item__primary-image')
    const secondary = wrapper.querySelector('.product-item__secondary-image')

    if (!primary || !secondary) return

    window.requestAnimationFrame(() => {
      if (!wrapper.matches(':hover')) return

      const secondaryStyle = window.getComputedStyle(secondary)
      const secondaryOpacity = Number.parseFloat(secondaryStyle.opacity || '0')

      if (secondaryStyle.visibility === 'visible' && secondaryOpacity > 0.5) return

      primary.setAttribute('data-chicchok-hot-priority-inline-image', 'true')
      secondary.setAttribute('data-chicchok-hot-priority-inline-image', 'true')
      primary.style.visibility = 'hidden'
      primary.style.opacity = '0'
      secondary.style.visibility = 'visible'
      secondary.style.opacity = '1'
    })
  }

  function shouldIgnorePriorityClick(event) {
    return Boolean(
      event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button > 0 ||
        event.target.closest(
          'button,input,select,textarea,label,form,.product-item__quick-form,quick-buy-popover,quick-buy-drawer,[aria-controls],[data-action],.popover,.drawer'
        )
    )
  }

  function bindPriorityCard(card) {
    if (card.dataset.chicchokHotPriorityBound === 'true') return

    const wrapper = card.querySelector('.product-item__image-wrapper')

    if (wrapper) {
      wrapper.addEventListener('pointerenter', () => repairHoverImages(wrapper))
      wrapper.addEventListener('mouseenter', () => repairHoverImages(wrapper))
      wrapper.addEventListener('pointerleave', () => clearInlineImageState(wrapper))
      wrapper.addEventListener('mouseleave', () => clearInlineImageState(wrapper))
    }

    card.addEventListener(
      'click',
      event => {
        if (shouldIgnorePriorityClick(event)) return

        const link = event.target.closest('a[href*="/products/"]')

        if (!link) return

        event.preventDefault()
        window.location.href = link.href
      },
      true
    )

    card.dataset.chicchokHotPriorityBound = 'true'
  }

  function repairEmptyPopupFrame() {
    const frame = document.getElementById('popup-shadow-root')

    if (!frame) return

    const src = (frame.getAttribute('src') || '').trim()

    if (src && src !== 'about:blank') return

    const rect = frame.getBoundingClientRect()
    const coversViewport = rect.width >= window.innerWidth * 0.8 && rect.height >= window.innerHeight * 0.8

    if (!coversViewport) return

    try {
      const text =
        frame.contentDocument && frame.contentDocument.body
          ? frame.contentDocument.body.innerText.trim()
          : ''
      const hasVisibleControls =
        frame.contentDocument &&
        frame.contentDocument.body &&
        frame.contentDocument.body.querySelector('button,a,input,select,textarea,[role="button"]')

      if (text || hasVisibleControls) return
    } catch (error) {
      return
    }

    frame.style.pointerEvents = 'none'
  }

  function repairEmptyChatWidgetHost() {
    const widget = document.querySelector('salesmartly-chat-widget')

    if (!widget) return

    const hasText = widget.innerText && widget.innerText.trim()
    const hasControls = widget.querySelector('button,a,input,select,textarea,[role="button"]')
    const hasShadowContent = widget.shadowRoot && widget.shadowRoot.children.length > 0

    if (hasText || hasControls || hasShadowContent) return

    widget.style.pointerEvents = 'none'
  }

  function repairBlockingOverlays() {
    repairEmptyPopupFrame()
    repairEmptyChatWidgetHost()
  }

  function scheduleBlockingOverlayRepairs() {
    const delays = [250, 1000, 2500, 5000, 8000, 12000, 15000]
    let repairRuns = 0

    delays.forEach(delay => {
      window.setTimeout(repairBlockingOverlays, delay)
    })

    const repairInterval = window.setInterval(() => {
      repairBlockingOverlays()
      repairRuns += 1

      if (repairRuns >= 80) {
        window.clearInterval(repairInterval)
      }
    }, 250)

    window.addEventListener('scroll', repairBlockingOverlays, { passive: true })
    window.addEventListener('resize', repairBlockingOverlays)
  }

  function repairPriorityInteractions() {
    if (!isHotCollectionPage() || !hasPriorityQuery()) return

    if (!document.querySelector(prioritySelector)) {
      schedulePriorityHydration()
      repairBlockingOverlays()
      return
    }

    document.querySelectorAll(prioritySelector).forEach(bindPriorityCard)
    repairBlockingOverlays()
    markPriorityGridReady()
  }

  if (!isHotCollectionPage() || !hasPriorityQuery()) return

  repairPriorityInteractions()
  scheduleBlockingOverlayRepairs()

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', repairPriorityInteractions)
  }

  window.setTimeout(repairPriorityInteractions, 250)
  window.setTimeout(repairPriorityInteractions, 1000)
  window.setTimeout(markPriorityGridReady, 12000)

  new MutationObserver(repairPriorityInteractions).observe(document.documentElement, {
    childList: true,
    subtree: true,
  })
})()
